<template>
  <BaseModal :isOpen="open" maxWidth="xl" @close="closeModal(true)">
    <form @submit.prevent="handleSubmit" class="relative">
      <header class="flex items-start justify-between">
        <div>
          <h2 class="text-2xl font-semibold text-primary-dark-text">
            {{ mode === "edit" ? "Edit Space" : "Add Space" }}
          </h2>
          <p class="mt-2 text-sm text-gray-500">
            {{
              mode === "edit"
                ? "Edit space details below."
                : "After adding spaces will be listed in the table"
            }}
          </p>
        </div>
      </header>

      <div class="mt-10 space-y-8">
        <div>
          <label
            for="space-name"
            class="text-sm font-semibold text-primary-dark-text"
            >Space Name</label
          >
          <input
            type="text"
            name="space-name"
            v-model="name"
            id="space-name"
            placeholder="Enter your space name"
            class="mt-2 block w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-primary-dark-text placeholder:text-gray-400 shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          <p v-if="nameError" class="mt-2 text-sm text-red-500">
            {{ nameError }}
          </p>
        </div>

        <div>
          <label
            for="select-users"
            class="text-sm font-semibold text-primary-dark-text"
            >Select users for space</label
          >
          <div ref="userSelector" class="relative mt-2">
            <button
              type="button"
              @click="toggleUserSelector"
              :class="[
                'flex w-full items-center justify-between rounded-lg border px-4 py-3 text-sm text-start transition focus:outline-none focus:ring-2 focus:ring-primary/30',
                selectedOptionsValidation
                  ? 'border-red-500 bg-red-50 text-red-600'
                  : 'border-gray-200 bg-gray-50 text-primary-dark-text hover:bg-gray-100',
              ]"
            >
              <span class="truncate">
                {{ selectedUsersText }}
              </span>
              <svg
                :class="[
                  'h-4 w-4 text-gray-400 transition-transform',
                  showUserSelector ? 'rotate-180' : '',
                ]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            <div
              v-if="showUserSelector"
              class="absolute left-0 top-full z-20 mt-2 w-full rounded-lg border border-gray-200 bg-white shadow-lg"
            >
              <ul class="max-h-60 overflow-y-auto py-2">
                <li v-for="option in options" :key="option.id" class="px-2">
                  <button
                    type="button"
                    @click="handleOptionToggle(option)"
                    :class="[
                      'flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm transition',
                      isOptionSelected(option)
                        ? 'bg-primary/10 text-primary-neutral'
                        : 'text-gray-600 hover:bg-gray-50',
                    ]"
                  >
                    <span class="text-left">{{ option.name }}</span>
                    <svg
                      v-if="
                        isOptionSelected(option) && option.id !== 'selectAll'
                      "
                      class="h-4 w-4 text-primary"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <p v-if="selectedOptionsValidation" class="mt-2 text-sm text-red-500">
            {{ selectedOptionsValidation }}
          </p>
        </div>

        <div>
          <label
            for="details"
            class="text-sm font-semibold text-primary-dark-text"
            >Details</label
          >
          <div class="mt-2">
            <QuillEditor
              v-model:content="details"
              contentType="html"
              theme="snow"
              placeholder="Add details"
              :options="editorOptions"
              style="height: 200px"
              class="rounded-lg border border-gray-200 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/30"
            />
            <div
              class="flex items-center justify-end px-4 py-2 text-gray-400 border-t border-gray-100"
            >
              <span class="text-xs font-medium">
                {{ detailsLength }} / 500
              </span>
            </div>
          </div>
          <p class="mt-2 text-xs text-gray-400">Maximum 500 characters</p>
        </div>
      </div>

      <p v-if="errorMessage" class="mt-4 text-sm text-red-500">
        {{ errorMessage }}
      </p>

      <div
        class="mt-10 flex items-center justify-end gap-6 border-t border-gray-100 pt-6"
      >
        <button
          @click="closeModal"
          type="button"
          class="text-sm font-semibold text-primary-dark-text transition hover:text-primary-neutral"
          :disabled="isSubmitting"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="rounded-lg bg-btn-gradient px-7 py-3 text-sm font-semibold text-white shadow-lg transition hover:opacity-90 disabled:opacity-60"
          :disabled="isSubmitting"
        >
          Save
        </button>
      </div>

      <div
        v-if="loader"
        class="absolute inset-0 flex items-center justify-center rounded-lg bg-white/60"
      >
        <div class="spinner"></div>
      </div>
    </form>
  </BaseModal>
</template>

<script>
import { getTargetUsers } from "../services/targetUsers";
import { createSpace, updateSpace } from "../services/space";
import { QuillEditor } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import BaseModal from "@/components/ui/BaseModal.vue";

export default {
  components: {
    QuillEditor,
    BaseModal,
  },
  data() {
    return {
      selectedOptions: [],
      options: [],
      selectedOptionsValidation: "",
      name: "",
      details: "",
      nameError: "",
      isSubmitting: false,
      errorMessage: "",
      loader: false,
      targetUsers: [],
      showUserSelector: false,
      editorOptions: {
        modules: {
          toolbar: [
            ["bold", "italic", "underline"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link"],
          ],
        },
        placeholder: "Add details...",
      },
    };
  },
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    space: {
      type: Object,
      default: null,
    },
    mode: {
      type: String,
      required: true,
    },
  },
  mounted() {
    this.getTargetUsers(true);
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  watch: {
    async open(newVal) {
      if (!newVal) {
        this.resetForm();
        this.isSubmitting = false;
        this.loader = false;
      } else {
        this.errorMessage = "";
        this.nameError = "";
        this.selectedOptionsValidation = "";
        // Use nextTick to ensure props are updated
        await this.$nextTick();
        // If editing, populate form data
        if (this.mode === "edit" && this.space) {
          await this.populateFormForEdit();
        }
      }
    },
    space: {
      immediate: true,
      async handler(newValue) {
        if (newValue && this.mode === "edit" && this.open) {
          await this.populateFormForEdit();
        } else if (!newValue && !this.open) {
          this.resetForm();
        }
      },
    },
    mode: {
      async handler(newValue) {
        if (newValue === "edit" && this.space && this.open) {
          await this.populateFormForEdit();
        }
      },
    },
  },
  computed: {
    detailsLength() {
      if (!this.details) return 0;
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = this.details;
      return tempDiv.textContent || tempDiv.innerText || 0;
    },
    selectedUsersText() {
      if (this.selectedOptions.length === 0) {
        return "Select target users";
      }

      if (this.selectedOptions[0]?.id === "selectAll") {
        return "All target users";
      }

      if (this.selectedOptions.length === 1) {
        return this.selectedOptions[0].name;
      }

      return `${this.selectedOptions.length} users selected`;
    },
  },
  methods: {
    resetForm() {
      this.selectedOptions = [];
      this.options = [];
      this.name = "";
      this.details = "";
      this.nameError = "";
      this.errorMessage = "";
      this.selectedOptionsValidation = "";
      this.showUserSelector = false;
    },
    closeModal(force = false) {
      if (this.isSubmitting && !force) {
        return;
      }

      this.showUserSelector = false;
      this.$emit("close");
    },
    mapSelectedUsers(selectedOptions, options) {
      const selectedArray = Array.from(selectedOptions);
      const optionsArray = Array.from(options);

      const optionIds = new Set(optionsArray.map((user) => user.id));
      if (selectedArray.length === 0) {
        this.selectedOptionsValidation = "Need to select at least one user.";
        return null;
      }

      this.selectedOptionsValidation = "";

      if (selectedArray[0].id === "selectAll") {
        return optionsArray.slice(1);
      }

      return selectedArray
        .filter((user) => optionIds.has(user.id))
        .map((user) => ({
          id: user.id,
          email: user.email,
          username: user.username,
          firstName: user.firstName,
          lastName: user.lastName,
          avatarUrl: user.avatarUrl,
          domainId: user.domainId,
          chatSpaceId: user.chatSpaceId,
          isActive: user.isActive,
          chatId: user.chatId,
        }));
    },
    toggleUserSelector() {
      this.showUserSelector = !this.showUserSelector;
      if (this.showUserSelector) {
        this.handleMultiselectOpen();
      }
    },
    handleClickOutside(event) {
      const selector = this.$refs.userSelector;
      if (!selector) {
        return;
      }

      if (this.showUserSelector && !selector.contains(event.target)) {
        this.showUserSelector = false;
      }
    },
    isOptionSelected(option) {
      return this.selectedOptions.some((selected) => selected.id === option.id);
    },
    handleOptionToggle(option) {
      if (option.id === "selectAll") {
        const isSelectAllSelected = this.isOptionSelected(option);
        this.selectedOptions = isSelectAllSelected ? [] : [option];
        this.showUserSelector = false;
        return;
      }

      const isSelected = this.isOptionSelected(option);
      const filteredWithoutSelectAll = this.selectedOptions.filter(
        (selected) => selected.id !== "selectAll"
      );

      if (isSelected) {
        this.selectedOptions = filteredWithoutSelectAll.filter(
          (selected) => selected.id !== option.id
        );
      } else {
        this.selectedOptions = [...filteredWithoutSelectAll, option];
      }
    },
    async handleSubmit() {
      this.nameError = "";

      if (!this.name) {
        this.nameError = "Space name is required.";
        return;
      }

      const selectedUsers = this.mapSelectedUsers(
        this.selectedOptions,
        this.options
      );

      if (!Array.isArray(selectedUsers) || selectedUsers.length === 0) {
        return;
      }

      const spaceData = {
        name: this.name,
        details: this.details,
        members: selectedUsers,
      };

      try {
        this.isSubmitting = true;
        this.loader = true;
        if (this.mode === "edit") {
          await updateSpace(this.space.id, spaceData);
          this.$emit("spaceUpdated");
          this.$emit("alert", {
            message: "Space updated successfully.",
            type: "success",
          });
        } else {
          await createSpace(spaceData);
          this.$emit("spaceAdded");
          this.$emit("alert", {
            message: "Space successfully created.",
            type: "success",
          });
        }
        this.$emit("close");
        this.resetForm();
      } catch (error) {
        if (
          error.response &&
          error.response.data &&
          error.response.data.detail &&
          this.selectedOptionsValidation == ""
        ) {
          this.errorMessage =
            "Your selected user email is not valid or not used in google chat.";
        }
      } finally {
        this.isSubmitting = false;
        this.loader = false;
      }
    },
    handleMultiselectOpen() {
      if (this.targetUsers) {
        this.options = this.targetUsers.map((user) => ({
          id: user.id,
          lastName: user.lastName,
          firstName: user.firstName,
          email: user.email,
          username: user.username,
          avatarUrl: user.avatarUrl,
          domainId: user.domainId,
          chatSpaceId: user.chatSpaceId,
          isActive: user.isActive,
          chatId: user.chatId,
          name: `${user.firstName} ${user.lastName}, ${user.username}, ${user.email}`,
        }));
        this.options.unshift({ id: "selectAll", name: "Select All" });
      } else {
        this.options = [];
      }
    },
    async getTargetUsers(isActive = true) {
      try {
        const users = await getTargetUsers(isActive);
        this.targetUsers = Array.isArray(users) ? users : [];
      } catch {
        this.errorMessage =
          "Unable to get members from space. Contact support.";
        return;
      }
    },
    async populateFormForEdit() {
      if (!this.space || this.mode !== "edit") {
        return;
      }

      this.name = this.space.name || "";
      this.details = this.space.details || "";

      if (this.targetUsers.length === 0) {
        await this.getTargetUsers();
      }

      if (this.targetUsers && this.targetUsers.length > 0) {
        this.options = this.targetUsers.map((user) => ({
          id: user.id,
          lastName: user.lastName,
          firstName: user.firstName,
          email: user.email,
          username: user.username,
          avatarUrl: user.avatarUrl,
          domainId: user.domainId,
          chatSpaceId: user.chatSpaceId,
          isActive: user.isActive,
          chatId: user.chatId,
          name: `${user.firstName} ${user.lastName}, ${user.username}, ${user.email}`,
        }));
        this.options.unshift({ id: "selectAll", name: "Select All" });
      }

      if (this.space.members && Array.isArray(this.space.members)) {
        this.selectedOptions = this.space.members.map((member) => ({
          id: member.id,
          lastName: member.lastName,
          firstName: member.firstName,
          email: member.email,
          username: member.username,
          avatarUrl: member.avatarUrl,
          domainId: member.domainId,
          chatSpaceId: member.chatSpaceId,
          isActive: member.isActive,
          chatId: member.chatId,
          name: `${member.firstName} ${member.lastName}, ${member.username}, ${member.email}`,
        }));
      }
    },
    populateOptionsAndSelectedMembers() {
      if (this.targetUsers && this.targetUsers.length > 0) {
        this.options = this.targetUsers.map((user) => ({
          id: user.id,
          lastName: user.lastName,
          firstName: user.firstName,
          email: user.email,
          username: user.username,
          avatarUrl: user.avatarUrl,
          domainId: user.domainId,
          chatSpaceId: user.chatSpaceId,
          isActive: user.isActive,
          chatId: user.chatId,
          name: `${user.firstName} ${user.lastName}, ${user.username}, ${user.email}`,
        }));
        this.options.unshift({ id: "selectAll", name: "Select All" });
      }

      if (
        this.space &&
        this.space.members &&
        Array.isArray(this.space.members)
      ) {
        this.selectedOptions = this.space.members.map((member) => ({
          id: member.id,
          lastName: member.lastName,
          firstName: member.firstName,
          email: member.email,
          username: member.username,
          avatarUrl: member.avatarUrl,
          domainId: member.domainId,
          chatSpaceId: member.chatSpaceId,
          isActive: member.isActive,
          chatId: member.chatId,
          name: `${member.firstName} ${member.lastName}, ${member.username}, ${member.email}`,
        }));
      }
    },
  },
};
</script>

<style scoped>
input,
textarea {
  width: 100%;
}

.spinner {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 9999px;
  border-width: 3px;
  border-style: solid;
  border-color: rgba(143, 86, 254, 0.35);
  border-top-color: #af56fe;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Quill editor custom styles */
:deep(.ql-toolbar) {
  border-top: none;
  border-left: none;
  border-right: none;
  border-bottom: 1px solid #e5e7eb;
  border-radius: 0.5rem 0.5rem 0 0;
}

:deep(.ql-container) {
  border-left: none;
  border-right: none;
  border-bottom: none;
  border-radius: 0 0 0.5rem 0.5rem;
  font-size: 0.875rem;
}

:deep(.ql-editor) {
  padding: 1rem;
  min-height: 120px;
  color: #111827;
}

:deep(.ql-editor.ql-blank::before) {
  color: #9ca3af;
  font-style: normal;
}

:deep(.ql-toolbar .ql-stroke) {
  stroke: #6b7280;
}

:deep(.ql-toolbar .ql-fill) {
  fill: #6b7280;
}

:deep(.ql-toolbar .ql-picker-label) {
  color: #6b7280;
}
</style>
